import React, { useEffect, useState } from "react";
import { GetCurrentStoreData, WebsitesState } from "../../store/websites/types";
import { connect } from "react-redux";
import { getCurrentStoreData } from "../../store/websites/thunks";
import { getPathArray } from "../../helpers/getPathArray";
import { RouteComponentProps, withRouter } from "react-router-dom";
import _ from "lodash";
import { deleteAllPageDynamicData } from "../../store/websitePages/actions";
import { getActiveDomain } from "../../helpers/websites/getActiveDomain";
import SpinnerBox from "../dashboard/SpinnerBox";
import { getJwtTokenAsync } from "../../helpers/async/getJwtTokenAsync";
import { AuthState } from "../../store/auth/types";
import "./AgentIframe.scss";
import { Button } from "antd";
import { getSubdomain } from "../../helpers/custom_domain/getSubdomain";
import {
  clearOpenedAgents,
  incrementAgentIframesRenderKey,
} from "../../store/websites/actions";
import {
  setIsSettingsModalVisible,
  setUserStateData,
} from "../../store/user/actions";
import { SetUserStateData } from "../../store/user/types";
import ResendConfirmationButton from "../dashboard/ResendConfirmationButton";

interface Props extends RouteComponentProps {
  auth: AuthState;
  history: any;
  websites: WebsitesState;
  getCurrentStoreData: GetCurrentStoreData;
  deleteAllPageDynamicData: any;
  clearOpenedAgents: any;
  setUserStateData: SetUserStateData;
  setIsSettingsModalVisible: any;

  agentData: any;
}

interface ErrorScreenProps extends Props {
  isVisible: boolean;
  agentData: any;

  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  buttonText?: string | React.ReactNode;
  buttonOnClick?: () => void;
}
const ErrorScreen = (props: ErrorScreenProps) => {
  const {
    isVisible,
    agentData,
    title,
    description,
    buttonText,
    buttonOnClick,
  } = props;
  const { currentWebsite } = props.getCurrentStoreData();
  return (
    <div
      style={{
        display: isVisible ? "flex" : "none",
      }}
      className="agent-domain-verify"
    >
      <div className="agent-domain-verify__container">
        <div className="agent-domain-verify__content">
          {title && <h3 style={{ marginBottom: 4, marginTop: 0 }}>{title}</h3>}
          {description && <div style={{ lineHeight: 1.5 }}>{description}</div>}
          {buttonText && (
            <div style={{ marginTop: 12 }}>
              <Button
                type="primary"
                onClick={() => {
                  if (buttonOnClick) {
                    buttonOnClick();
                  }
                }}
              >
                {buttonText}
              </Button>
            </div>
          )}
        </div>
        <div className="agent-domain-verify__screenshot-container">
          <img
            src={agentData.screenshot}
            className="agent-domain-verify__screenshot"
          />
        </div>
      </div>
    </div>
  );
};

const AgentIframe = (props: Props) => {
  const { agentData } = props;
  const { currentWebsite } = props.getCurrentStoreData();
  const [isLoading, setIsLoading] = useState(true);
  const [jwtToken, setJwtToken] = useState<{
    token: string;
    status: string;
    error: string;
  }>(null);

  useEffect(() => {
    console.log("MOUNT AGENT: ", agentData.title);
    const { currentWebsite } = props.getCurrentStoreData();
    const subdomain = getSubdomain(currentWebsite.custom_domain);

    getJwtTokenAsync({
      accessToken: props.auth.accessToken,
      websiteId: _.get(currentWebsite, "id"),
      subdomain,
    })
      .then((response) => {
        setJwtToken(response.data.token);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);
  }, [_.get(currentWebsite, "id")]);

  if (!currentWebsite) return null;

  const pathArray = getPathArray(props.history.location.pathname);
  const currentAppSection = pathArray[1];
  const currentAgentId = pathArray[2];

  const isVisible =
    currentAppSection === "agents" && currentAgentId === agentData.id;

  if (!jwtToken) {
    if (isVisible) {
      return (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <SpinnerBox text="Initializing agent..." />
        </div>
      );
    }
    return null;
  }
  const iframeSrc = agentData.iframeSrc.replace(
    "{{JWT_TOKEN}}",
    jwtToken.token
  );

  if (jwtToken.status === "error") {
    if (jwtToken.error === "domain_not_verified") {
      return (
        <ErrorScreen
          isVisible={isVisible}
          agentData={agentData}
          {...props}
          title="Domain not verified"
          description={
            <>
              <div>
                We need to verify that you own {getActiveDomain(currentWebsite)}
                . Please add the{" "}
                <a
                  className="underlined_link"
                  onClick={() => {
                    props.setUserStateData({
                      settings: "customDomain",
                    });
                    props.setIsSettingsModalVisible(true);
                  }}
                >
                  required DNS records
                </a>{" "}
                and reload the agent.
              </div>
            </>
          }
          buttonText="Reload agent"
          buttonOnClick={() => {
            props.clearOpenedAgents();
          }}
        />
      );
    }
    if (jwtToken.error === "no_custom_domain") {
      return (
        <ErrorScreen
          isVisible={isVisible}
          agentData={agentData}
          {...props}
          title="Custom domain required"
          description={<>You need to add custom domain to use agents.</>}
          buttonText="Add custom domain"
          buttonOnClick={() => {
            props.setUserStateData({
              settings: "customDomain",
            });
            props.setIsSettingsModalVisible(true);
          }}
        />
      );
    }
    if (jwtToken.error === "email_not_verified") {
      return (
        <ErrorScreen
          isVisible={isVisible}
          agentData={agentData}
          {...props}
          title="Email not verified"
          description={
            <div>
              <p style={{ marginBottom: 10 }}>
                You need to verify your email to use agents.
              </p>
              <ResendConfirmationButton type="button">
                Resend verification
              </ResendConfirmationButton>
            </div>
          }
        />
      );
    }
    if (jwtToken.error === "legacy_setup") {
      return (
        <ErrorScreen
          isVisible={isVisible}
          agentData={agentData}
          {...props}
          title="Not available on legacy domains"
          description={
            <>
              Agents are not available on legacy domain setups. Please update
              your domain setup in the settings.
            </>
          }
          buttonText="Update"
          buttonOnClick={() => {
            props.setUserStateData({
              settings: "customDomain",
            });
            props.setIsSettingsModalVisible(true);
          }}
        />
      );
    }
    if (jwtToken.error === "website_not_owned") {
      return (
        <ErrorScreen
          isVisible={isVisible}
          agentData={agentData}
          {...props}
          title="Not available on shared websites"
          description={
            <>Agents can't be used on websites owned by other users.</>
          }
        />
      );
    }
    if (jwtToken.error === "free_plan") {
      return (
        <ErrorScreen
          isVisible={isVisible}
          agentData={agentData}
          {...props}
          title="Not available on free plan"
          description={<>Please upgrade to a paid plan to use agents.</>}
          buttonText="Upgrade"
          buttonOnClick={() => {
            props.setUserStateData({
              settings: "account__plan",
            });
            props.setIsSettingsModalVisible(true);
          }}
        />
      );
    }
  }

  return (
    <div
      style={{
        position: "relative",
        padding: "8px 8px 8px 0",
        minHeight: "100vh",
        lineHeight: 0,
        display: isVisible ? "block" : "none",
      }}
      key={_.get(currentWebsite, "id")}
    >
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <SpinnerBox text="Loading agent..." />
        </div>
      )}
      <iframe
        style={{
          width: "100%",
          height: "calc(100vh - 16px)",
          boxShadow:
            "rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.1) 0px 1px 2px -1px",
          border: "1px solid rgb(228, 228, 231)",
          borderRadius: "12px",
          opacity: isLoading ? 0 : 1,
        }}
        src={iframeSrc}
        onLoad={() => setIsLoading(false)}
        id={`agent-iframe-${agentData.id}`}
      ></iframe>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    websites: state.websites,
    auth: state.auth,
  };
};
export default connect(mapStateToProps, {
  getCurrentStoreData,
  deleteAllPageDynamicData,
  clearOpenedAgents,
  setUserStateData,
  setIsSettingsModalVisible,
})(withRouter(AgentIframe));
